/**
 * batch-66 verkoop-extractie (FO-493) - welk opmaakprofiel hoort bij deze
 * factuur? Puur: profielen + PDF-tekst + mailafzender in, het best passende
 * profiel uit (of null = generiek).
 *
 * Tekstkenmerken worden GEPLET vergeleken (geen spaties, geen hoofdletters),
 * omdat de tekstlaag kerning-spaties bevat ("Uitgifte datum"). Is er tekst, dan
 * beslist de tekst: een bekende afzender met een andere opmaak (bv. een
 * kostenfactuur van dezelfde partij) krijgt het profiel NIET. Zonder tekstlaag
 * (scan) telt alleen de afzender.
 */

import type { ExtractieProfielHerkenning } from '@streep/types'
import { pletKlein } from './tekst'

export interface ExtractieProfielKandidaat {
  id: string
  sleutel: string
  naam: string
  actief: boolean
  prioriteit: number
  herkenning: ExtractieProfielHerkenning
}

export interface ProfielHerkenning<T extends ExtractieProfielKandidaat> {
  profiel: T
  /** Herkend op de PDF-tekst (anders alleen op de afzender, bij een scan). */
  viaTekst: boolean
  viaAfzender: boolean
  /** De tekstkenmerken die gevonden zijn. */
  kenmerken: string[]
  reden: string
}

/** Plafond op de tekst die we doorzoeken (open mailbox). */
const MAX_TEKST = 200_000

/** Het kale adres uit "Naam <adres>" of een kaal adres, in kleine letters. */
function adresVan(afzender: string | null): string | null {
  if (!afzender) return null
  const m = /<([^>]+)>/.exec(afzender)
  const adres = (m ? (m[1] ?? '') : afzender).trim().toLowerCase()
  return adres.includes('@') ? adres : null
}

/** Hoort dit adres bij het domein (exact of een subdomein)? */
export function afzenderHoortBijDomein(afzender: string | null, domein: string): boolean {
  const adres = adresVan(afzender)
  const d = domein.trim().toLowerCase().replace(/^@/, '')
  if (!adres || d.length === 0) return false
  const adresDomein = adres.slice(adres.lastIndexOf('@') + 1)
  return adresDomein === d || adresDomein.endsWith(`.${d}`)
}

/**
 * Kies het profiel. Het hoogst scorende profiel wint; bij een gelijke score het
 * profiel met de laagste `prioriteit`, daarna op sleutel (stabiel).
 */
export function kiesExtractieProfiel<T extends ExtractieProfielKandidaat>(
  profielen: readonly T[],
  invoer: { tekst: string | null; afzender: string | null },
): ProfielHerkenning<T> | null {
  const tekst = invoer.tekst && invoer.tekst.trim().length > 0 ? invoer.tekst : null
  const tekstVorm = tekst ? pletKlein(tekst.slice(0, MAX_TEKST)) : null

  let beste: { uitkomst: ProfielHerkenning<T>; score: number } | null = null
  for (const profiel of profielen) {
    if (!profiel.actief) continue
    const h = profiel.herkenning
    const viaAfzender = h.afzenderDomeinen.some((d) => afzenderHoortBijDomein(invoer.afzender, d))
    const heeftTekstregels = h.verplichteKenmerken.length > 0 || h.minimaalKenmerken > 0

    let viaTekst = false
    let gevonden: string[] = []
    if (tekstVorm !== null && heeftTekstregels) {
      const staatErin = (kenmerk: string) => {
        const vorm = pletKlein(kenmerk)
        return vorm.length > 0 && tekstVorm.includes(vorm)
      }
      const verplichtOk = h.verplichteKenmerken.every(staatErin)
      const overige = h.kenmerken.filter(staatErin)
      viaTekst = verplichtOk && overige.length >= h.minimaalKenmerken
      gevonden = viaTekst ? [...h.verplichteKenmerken, ...overige] : []
    }

    // Met tekst beslist de tekst; zonder tekst (scan) alleen de afzender.
    const past = tekstVorm !== null ? viaTekst : viaAfzender
    if (!past) continue

    const score = (viaTekst ? 1000 + h.verplichteKenmerken.length * 10 + gevonden.length : 0) + (viaAfzender ? 5 : 0)
    const reden = viaTekst
      ? `Herkend op ${gevonden.length} tekstkenmerk${gevonden.length === 1 ? '' : 'en'}${viaAfzender ? ' en de afzender' : ''}.`
      : 'Herkend op de afzender (de PDF heeft geen leesbare tekstlaag).'
    const uitkomst: ProfielHerkenning<T> = { profiel, viaTekst, viaAfzender, kenmerken: gevonden, reden }
    if (
      beste === null ||
      score > beste.score ||
      (score === beste.score &&
        (profiel.prioriteit < beste.uitkomst.profiel.prioriteit ||
          (profiel.prioriteit === beste.uitkomst.profiel.prioriteit &&
            profiel.sleutel < beste.uitkomst.profiel.sleutel)))
    ) {
      beste = { uitkomst, score }
    }
  }
  return beste?.uitkomst ?? null
}
